import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AccessAPI, { FeatureKey, UserAccess } from '../api/access';
import { useAuth } from './AuthContext';

interface AccessContextValue {
  access: UserAccess | null;
  loaded: boolean;
  isAdmin: boolean;
  hasFeature: (feature: FeatureKey) => boolean;
  canSeeTab: (tab: string) => boolean;
  refresh: () => Promise<void>;
}

const fallback: AccessContextValue = {
  access: null,
  loaded: false,
  isAdmin: false,
  hasFeature: (feature) => feature === 'studio',
  canSeeTab: (tab) => tab === 'studio',
  refresh: async () => {},
};

const AccessContext = createContext<AccessContextValue>(fallback);

export function AccessProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [access, setAccess] = useState<UserAccess | null>(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setAccess(null);
      setLoaded(false);
      return;
    }
    const data = await AccessAPI.getMyAccess();
    setAccess(data);
    setLoaded(true);
  }, [isAuthenticated]);

  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated) {
      setAccess(null);
      setLoaded(false);
      return;
    }
    AccessAPI.getMyAccess()
      .then((data) => {
        if (!cancelled) {
          setAccess(data);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAccess({ user_id: '', is_admin: false, allowed_features: ['studio'], visible_tabs: ['studio'] });
          setLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const value = useMemo<AccessContextValue>(() => {
    const allowed = new Set(access?.allowed_features ?? ['studio']);
    const tabs = new Set(access?.visible_tabs ?? ['studio']);
    return {
      access,
      loaded,
      isAdmin: access?.is_admin ?? false,
      hasFeature: (feature) => Boolean(access?.is_admin) || allowed.has(feature),
      canSeeTab: (tab) => Boolean(access?.is_admin) || tabs.has(tab),
      refresh,
    };
  }, [access, loaded, refresh]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess(): AccessContextValue {
  return useContext(AccessContext);
}
